
import React, { createContext, useContext, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { db } from '../firebase';
import { collectionGroup, query, orderBy, limit, onSnapshot, getDoc } from 'firebase/firestore';
import { useAuth } from 'hooks/useAuth';

export const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();

  const setNotification = useCallback(({ message, type }) => {
    if (type === 'success') {
      toast.success(message, { duration: 4000 });
    } else if (type === 'error') {
      toast.error(message, { duration: 4000 });
    } else {
      toast(message, { duration: 4000 });
    }
  }, []);
  
  useEffect(() => {
    // ユーザーIDと匿名フラグのみを取得して依存配列に使用する
    // これにより、userオブジェクトの他のプロパティが変更されてもリスナーが再登録されるのを防ぐ
    const uid = user?.uid;
    const isAnonymous = user?.isAnonymous;

    if (!uid || isAnonymous) return;

    // リスナー設定時の初回スナップショット（既存データ）を無視するためのフラグ
    let isFirstSnapshot = true;

    const commentsQuery = query(
      collectionGroup(db, 'comments'),
      orderBy('timestamp', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(commentsQuery, (snapshot) => {
      // 初回のデータ読み込み時は通知を出さない
      if (isFirstSnapshot) {
        isFirstSnapshot = false;
        return;
      }

      snapshot.docChanges().forEach(async (change) => {
        if (change.type === 'added') {
          const commentData = change.doc.data();
          // 自分のコメントは通知しない
          if (commentData.uid === uid) return;

          const recordRef = change.doc.ref.parent.parent;
          if (!recordRef) return;
          
          try {
            const recordSnap = await getDoc(recordRef);
            if (recordSnap.exists()) {
              const recordData = recordSnap.data();
              // 自分の記録へのコメントのみ通知
              if (recordData.userId === uid) {
                setNotification({
                  message: `${commentData.author || '匿名さん'}さんが「${recordData.title || '無題の記録'}」にコメントしました！`,
                  type: 'success'
                });
              }
            }
          } catch (error) {
            console.error("通知処理中にエラーが発生しました:", error);
          }
        }
      });
    }, (error) => {
      console.error("コメントの監視中にエラーが発生しました: ", error);
    });

    return () => unsubscribe();
  }, [user?.uid, user?.isAnonymous, setNotification]);


  return (
    <NotificationContext.Provider value={{ setNotification }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => useContext(NotificationContext);
